import styled from 'styled-components'
import HumanIcon from '../asset/HumanIcon'
import BlockIcon from '../asset/BlockIocn'
import { ISeasonalNum } from '../util/algorithm/algoInterface'
import UnionUp from '../asset/UnionUp'
import UnionDown from '../asset/UnionDown'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px #00000029;
`

const Head = styled.div`
  display: flex;
  flex-direction: column;
  background: #373a40;
  gap: 8px;
  border-radius: 0px 0px 4px 4px;
`

const StatusBoardWrapper = styled.div`
  display: flex;
  padding: 16px 24px;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 0px 0px 4px 4px;
  border-top: 1px solid var(--gray60, #808080);
  background: var(--bluegray100, #373a40);
`

const StatusBoardDetail = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
`

const IconTextFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`

const IconTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const TextDetail = styled.div<TextProps>`
  color: ${(props) => (props.fontColor ? props.fontColor : '#ffffff')};
  font-family: SUIT;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '16px')};
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

const PriceTableFrame = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const ChargeUnitTableBody = styled(TableBody)<{ $hasHeaderGap: boolean }>`
  margin-top: ${(props) => (props.$hasHeaderGap ? '14px' : '0px')};
`

const TableRowListWrapper = styled.div`
  display: flex;
  align-items: center;
`

const TotalLabelRowListWrapper = styled(TableRowListWrapper)`
  margin-top: 6px;
`

const TotalIncomeRowListWrapper = styled(TableRowListWrapper)`
  margin-top: 0px;
`

const RowDetail = styled.div<TableRowProps>`
  display: flex;
  width: 64px;
  padding: 4px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  background: ${(props) =>
    props.backgroundColor ? props.backgroundColor : '#4d5055'};
  ${(props) => props.borderBottom && `border-bottom: ${props.borderBottom};`}
  min-width: 50px;
`
const IncomeText = styled.div<TextProps>`
  color: ${(props) => (props.fontColor ? props.fontColor : '#e6e6e6')};
  font-family: SUIT;
  font-size: ${(props) => (props.fontSize ? props.fontSize : '12px')};
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: right;
  text-decoration: ${(props) =>
    props.textDecoration ? props.textDecoration : 'line-through'};
`

const IncomeColumnHeader = styled(RowDetail)`
  position: relative;
  width: 124px;
  min-width: 124px;
`

const IncomeHeaderWithUnit = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const IncomeHeaderLabel = styled(TextDetail)`
  min-width: 0;
  white-space: nowrap;
`

const IncomeUnitText = styled(TextDetail)`
  font-weight: 500;
  white-space: nowrap;
`

const IncomeColumnCell = styled(RowDetail)`
  box-sizing: border-box;
  width: 124px;
  min-width: 124px;
  align-items: flex-end;
  padding: 4px 12px 4px 0px;
`

const BenefitFrame = styled.div`
  display: inline-flex;
  position: relative;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`

const BenefitIconWrapper = styled.span`
  display: inline-flex;
  width: 10px;
  height: 10px;
  align-items: center;
  justify-content: center;
`

const BenefitText = styled(IncomeText)`
  text-decoration: none;
  white-space: nowrap;
`

const BenefitKoreanAmountText = styled(BenefitText)`
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  opacity: 0.68;
`

const EssProfitCard = styled.div<{ $negative?: boolean }>`
  display: flex;
  min-width: 148px;
  padding: 9px 12px;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  border-radius: 4px;
  border: 1px solid #5f636b;
  background: #41444a;
`

const EssProfitLabel = styled.div`
  color: #e6e6e6;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
`

const EssProfitValue = styled.div<{ $negative?: boolean }>`
  color: ${(props) => (props.$negative ? '#f36464' : '#57c9a7')};
  font-family: SUIT;
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  white-space: nowrap;
`

const EssProfitMeta = styled.div`
  color: #bfc3ca;
  font-family: SUIT;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
`

interface TextProps {
  fontSize?: string
  fontColor?: string
  textDecoration?: string
}

interface TableRowProps {
  backgroundColor?: string
  borderBottom?: string
  borderRadius?: string
}

interface Props {
  name?: string
  ratePlan?: string
  chartUnitResult?: any
  beforeIncome?: ISeasonalNum
  afterIncome?: ISeasonalNum
  re100Opt?: string
  essBusinessProfit?: number
  essProfitRate?: number
}

type SeasonKey = keyof ISeasonalNum

const seasons: { key: SeasonKey; label: string }[] = [
  { key: 'spr', label: '봄' },
  { key: 'sum', label: '여름' },
  { key: 'fall', label: '가을' },
  { key: 'win', label: '겨울' },
]

const isValidNumber = (value?: number) =>
  typeof value === 'number' && Number.isFinite(value)

const formatNumber = (value?: number, fallback = '-') => {
  const numberValue = Number(value)

  if (!Number.isFinite(numberValue)) {
    return fallback
  }

  return Number(numberValue.toFixed(0)).toLocaleString()
}

const formatKoreanManwon = (value?: number) => {
  const numericValue = Number(value)
  const manwonValue = Math.round(Math.abs(numericValue) / 10000)

  if (!Number.isFinite(numericValue)) {
    return undefined
  }

  const eokValue = Math.floor(manwonValue / 10000)
  const remainingManwon = manwonValue % 10000

  if (eokValue > 0 && remainingManwon > 0) {
    return `${eokValue.toLocaleString()}억 ${remainingManwon.toLocaleString()}만원`
  }

  if (eokValue > 0) {
    return `${eokValue.toLocaleString()}억원`
  }

  return `${manwonValue.toLocaleString()}만원`
}

const hasSeasonalValue = (value?: ISeasonalNum) =>
  value
    ? seasons.some(({ key }) => isValidNumber(value[key]) && value[key] !== 0)
    : false

const hasChargeUnitValue = (value?: any) =>
  seasons.some(({ key }) =>
    ['min', 'mid', 'max'].some(
      (load) => isValidNumber(value?.[key]?.[load]) && value[key][load] !== 0
    )
  )

const sumSeasonalValue = (value?: ISeasonalNum) =>
  value
    ? seasons.reduce((total, { key }) => total + (value[key] ?? 0), 0)
    : undefined

export const PowerSellBoard = (props: Props) => {
  const {
    name,
    ratePlan,
    chartUnitResult,
    beforeIncome,
    afterIncome,
    re100Opt,
    essBusinessProfit,
    essProfitRate,
  } = props
  const isRE100Achieved = re100Opt === 'achieve'
  const isChargeUnitCalculated = hasChargeUnitValue(chartUnitResult)
  const isIncomeCalculated =
    hasSeasonalValue(beforeIncome) || hasSeasonalValue(afterIncome)

  const getWidth = () => {
    const element = document.getElementById('income-header')
    const width = element?.offsetWidth
    return width
  }
  const maxWidth = getWidth()
  const beforeTotal = sumSeasonalValue(beforeIncome)
  const afterTotal = sumSeasonalValue(afterIncome)
  const formatChargeUnitNumber = (value?: number) =>
    isChargeUnitCalculated ? formatNumber(value) : '-'
  const formatIncomeNumber = (value?: number) =>
    isIncomeCalculated ? formatNumber(value) : '-'
  const isEssProfitCalculated = isValidNumber(essBusinessProfit)
  const essProfitAmount = Number(essBusinessProfit)
  const essProfitText = isEssProfitCalculated
    ? `${formatNumber(essProfitAmount)}원`
    : '-'
  const essProfitMetaText = isValidNumber(essProfitRate)
    ? `요금 설계 이익 중 ${formatNumber(essProfitRate)}%`
    : '요금 설계 이익 기준'
  const getBenefitValue = (beforeValue?: number, afterValue?: number) => {
    const beforeNumber = Number(beforeValue)
    const afterNumber = Number(afterValue)

    if (!Number.isFinite(beforeNumber) || !Number.isFinite(afterNumber)) {
      return undefined
    }

    return Number((beforeNumber - afterNumber).toFixed(0))
  }
  const renderBenefit = (value?: number, showKoreanAmount = false) => {
    if (!isIncomeCalculated || !isValidNumber(value)) {
      return (
        <BenefitText fontColor="#E6E6E6" fontSize="12px" textDecoration="none">
          -
        </BenefitText>
      )
    }

    const numericValue = Number(value)
    const koreanAmountText = showKoreanAmount
      ? formatKoreanManwon(numericValue)
      : undefined
    const fontColor =
      numericValue > 0 ? '#649bf3' : numericValue < 0 ? '#F36464' : '#E6E6E6'
    const icon =
      numericValue > 0 ? (
        <UnionDown color={fontColor} />
      ) : numericValue < 0 ? (
        <UnionUp color={fontColor} />
      ) : null

    return (
      <BenefitFrame>
        {icon && <BenefitIconWrapper>{icon}</BenefitIconWrapper>}
        <BenefitText
          fontColor={fontColor}
          fontSize="12px"
          textDecoration="none"
        >
          {formatNumber(Math.abs(numericValue))}
        </BenefitText>
        {koreanAmountText ? (
          <BenefitKoreanAmountText fontColor={fontColor} textDecoration="none">
            {koreanAmountText}
          </BenefitKoreanAmountText>
        ) : null}
      </BenefitFrame>
    )
  }
  const renderIncomeCells = (seasonKey?: SeasonKey) => {
    const beforeValue = seasonKey ? beforeIncome?.[seasonKey] : beforeTotal
    const afterValue = seasonKey ? afterIncome?.[seasonKey] : afterTotal
    const benefitValue = getBenefitValue(beforeValue, afterValue)

    return (
      <>
        <IncomeColumnCell backgroundColor="#373a40">
          <IncomeText fontSize="12px" fontColor="#E6E6E6" textDecoration="none">
            {formatIncomeNumber(beforeValue)}
          </IncomeText>
        </IncomeColumnCell>
        <IncomeColumnCell backgroundColor="#373a40">
          <IncomeText fontColor="#fff" fontSize="12px" textDecoration="none">
            {formatIncomeNumber(afterValue)}
          </IncomeText>
        </IncomeColumnCell>
        <IncomeColumnCell backgroundColor="#373a40">
          {renderBenefit(benefitValue, seasonKey === undefined)}
        </IncomeColumnCell>
      </>
    )
  }
  const renderIncomeRow = (seasonKey: SeasonKey) => {
    return (
      <TableRowListWrapper>{renderIncomeCells(seasonKey)}</TableRowListWrapper>
    )
  }

  return (
    <Wrapper>
      <Head>
        <StatusBoardWrapper>
          <StatusBoardDetail>
            <IconTextFrame>
              <IconTextWrapper>
                <HumanIcon />
                <TextDetail> {name || '- - -'}</TextDetail>
                <TextDetail>고객</TextDetail>
              </IconTextWrapper>
              <IconTextWrapper>
                <BlockIcon />
                <TextDetail> {ratePlan || '- - -'}</TextDetail>
              </IconTextWrapper>
            </IconTextFrame>
            <PriceTableFrame>
              <ChargeUnitTableBody $hasHeaderGap={isRE100Achieved}>
                <TableRowListWrapper>
                  <RowDetail
                    style={{
                      borderTopLeftRadius: '4px',
                      borderBottomLeftRadius: '4px',
                    }}
                  >
                    <TextDetail fontSize="10px" fontColor="#E6E6E6">
                      원/kWh
                    </TextDetail>
                  </RowDetail>
                  <RowDetail>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      경부하
                    </TextDetail>
                  </RowDetail>
                  <RowDetail>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      중간부하
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    style={{
                      borderTopRightRadius: '4px',
                      borderBottomRightRadius: '4px',
                    }}
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      최대부하
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TableRowListWrapper>
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      봄
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.spr?.min)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.spr?.mid)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.spr?.max)}
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TableRowListWrapper>
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      여름
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.sum?.min)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.sum?.mid)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.sum?.max)}
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TableRowListWrapper>
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      가을
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.fall?.min)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.fall?.mid)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.fall?.max)}
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TableRowListWrapper>
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      겨울
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.win?.min)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.win?.mid)}
                    </TextDetail>
                  </RowDetail>
                  <RowDetail
                    backgroundColor="#373a40"
                    borderBottom="0.5px solid  #CCC;"
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {formatChargeUnitNumber(chartUnitResult?.win?.max)}
                    </TextDetail>
                  </RowDetail>
                </TableRowListWrapper>
                <TotalLabelRowListWrapper>
                  <RowDetail style={{ visibility: 'hidden' }} />
                  <RowDetail style={{ visibility: 'hidden' }} />
                  <RowDetail style={{ visibility: 'hidden' }} />
                  <RowDetail style={{ borderRadius: '4px' }}>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      총합
                    </TextDetail>
                  </RowDetail>
                </TotalLabelRowListWrapper>
              </ChargeUnitTableBody>
              <TableBody>
                <TableRowListWrapper>
                  <IncomeColumnHeader
                    id="income-header"
                    style={{
                      borderTopLeftRadius: '4px',
                      borderBottomLeftRadius: '4px',
                      width: maxWidth ? `${maxWidth}px` : undefined,
                    }}
                  >
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {isRE100Achieved ? (
                        <>
                          수용가 기존요금 및
                          <br />
                          녹색 프리미엄
                        </>
                      ) : (
                        '수용가 기존요금'
                      )}
                    </TextDetail>
                  </IncomeColumnHeader>
                  <IncomeColumnHeader>
                    <TextDetail fontSize="12px" fontColor="#E6E6E6">
                      {isRE100Achieved ? (
                        <>
                          수용가 거래 후 요금 및
                          <br />
                          RE100 달성금액
                        </>
                      ) : (
                        '수용가 거래 후 요금'
                      )}
                    </TextDetail>
                  </IncomeColumnHeader>
                  <IncomeColumnHeader
                    style={{
                      borderTopRightRadius: '4px',
                      borderBottomRightRadius: '4px',
                    }}
                  >
                    <IncomeHeaderWithUnit>
                      <IncomeHeaderLabel fontSize="12px" fontColor="#E6E6E6">
                        수용가 요금 절감액
                      </IncomeHeaderLabel>
                      <IncomeUnitText fontSize="12px" fontColor="#E6E6E6">
                        [원]
                      </IncomeUnitText>
                    </IncomeHeaderWithUnit>
                  </IncomeColumnHeader>
                </TableRowListWrapper>
                {renderIncomeRow('spr')}
                {renderIncomeRow('sum')}
                {renderIncomeRow('fall')}
                {renderIncomeRow('win')}
                <TotalIncomeRowListWrapper>
                  {renderIncomeCells()}
                </TotalIncomeRowListWrapper>
              </TableBody>
              <EssProfitCard $negative={essProfitAmount < 0}>
                <EssProfitLabel>ESS 사업자 이익</EssProfitLabel>
                <EssProfitValue $negative={essProfitAmount < 0}>
                  {essProfitText}
                </EssProfitValue>
                <EssProfitMeta>{essProfitMetaText}</EssProfitMeta>
              </EssProfitCard>
            </PriceTableFrame>
          </StatusBoardDetail>
        </StatusBoardWrapper>
      </Head>
    </Wrapper>
  )
}
